import React from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import UFOSegment from '@atlassian/jira-ufo-segment';
import type { DefaultFallback, FallbackOptions } from '../../../common/types';

import { components } from '../../../common/ui/components';
import { CommandPaletteBody, NoResultsContainer } from '../../../common/ui/components/body';
import { getActiveCommandList } from '../../../common/utils/get-active-command-list';
import {
	useCommandPalette,
	useCommandPaletteRegistryCurrentAttribution,
	useMatches,
} from '../../../controllers';
import { usePerformMode } from '../../../controllers/use-perform-mode';
import { usePerformPendingCommandOnClose } from '../../../controllers/use-perform-pending-command-on-close';
import { Body } from './body';
import { CommandBodyErrorBoundary } from './body-error-boundary';
import { ERROR_FALLBACK_CONTAINER } from './constants';
import { Footer } from './footer';
import Header from './header';
import { ScreenReaderContent } from './screen-reader-content';
import type { ModalContentProps } from './types';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP = () => {};

export const ModalContent = ({
	defaultFallback,
	rootFallback,
	defaultPlaceholder,
	defaultFooterContent,
	onClose,
	onAnalytics,
}: ModalContentProps) => {
	const { total, fallback, results, renderAfterCommands, activeCommand } = useMatches({
		defaultFallback,
		rootFallback,
		onAnalytics,
	});

	const [{ expandedCommand }, { setChildCommands, setLoadingList }] = ff(
		'compal-issue-actions-very-high-priority_f5w5r',
	)
		? [{ expandedCommand: undefined }, { setChildCommands: NOOP, setLoadingList: NOOP }]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useCommandPalette();
	const [registryData] = useCommandPaletteRegistryCurrentAttribution();

	const renderBody = ff('compal-issue-actions-very-high-priority_f5w5r')
		? undefined
		: () => {
				const ExpandedBody =
					getActiveCommandList(expandedCommand, expandedCommand?.wasSecondaryTriggered)?.components
						?.Body || components.Body;
				if (ExpandedBody) {
					return (
						<ExpandedBody
							setChildResult={setChildCommands}
							setLoading={setLoadingList}
							fallback={fallback}
							results={results}
							total={total}
							renderAfterCommands={renderAfterCommands}
							onAnalytics={onAnalytics}
						/>
					);
				}
				return (
					<CommandPaletteBody
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						fallback={{ onNoResults: fallback as FallbackOptions }}
						results={results}
						total={total}
						renderAfterCommands={renderAfterCommands}
						onAnalytics={onAnalytics}
					/>
				);
			};

	usePerformPendingCommandOnClose();
	usePerformMode();

	// Typing as any so we can pass the prop to the UFO Segment using ffs without typescript/react errors
	return (
		<>
			<Header
				onClose={onClose}
				defaultPlaceholder={defaultPlaceholder}
				results={results}
				onAnalytics={onAnalytics}
				activeCommand={activeCommand}
			/>
			<UFOSegment name="command-palette-body">
				<CommandBodyErrorBoundary
					teamName={registryData?.registryData?.teamName || 'deliveroo'}
					attributes={registryData?.analyticsAttributes}
					fallback={() => (
						<NoResultsContainer data-testid={ERROR_FALLBACK_CONTAINER}>
							{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
							{(fallback as DefaultFallback)?.onError?.renderLabel?.() ||
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								(fallback as DefaultFallback)?.onError?.label}
						</NoResultsContainer>
					)}
					expandedCommand={expandedCommand}
				>
					{ff('compal-issue-actions-very-high-priority_f5w5r') ? (
						<Body
							fallback={fallback}
							results={results}
							total={total}
							renderAfterCommands={renderAfterCommands}
							onAnalytics={onAnalytics}
						/>
					) : (
						renderBody?.()
					)}
				</CommandBodyErrorBoundary>
			</UFOSegment>
			<ScreenReaderContent activeCommand={activeCommand} results={results} />
			<Footer defaultFooterContent={defaultFooterContent} activeCommand={activeCommand} />
		</>
	);
};
